import { inject, Injectable } from '@angular/core';
import { ConnectorDefinition, Product, UserWithSeat } from 'src/api';
import {
  AddonCatalogItem,
  ConnectorAddonCatalogItem,
  ContentAddonCatalogItem,
} from '../models/addon-catalog.types';
import { hasConnectorFeature } from '../constants/integration-features';
import {
  connectorBookingLimitExempt,
  isConnectorInFirmAddonSurface,
} from '../utils/connector-premium.util';
import { connectorIntegrationDisplayLabel } from '../utils/realm-connectivity.util';
import { connectorInstructionsManualUrl } from '../utils/connector-manifest.util';
import { SeatOptionsService } from './seat-options.service';
import { SeatUtilsService } from './seat-utils.service';
import { UserSeatService } from './user-seat.service';
import {
  firmHasAddOnEntitlementForPriceIds,
  firmHasAddOnForPriceIds,
  firmHeldAddOnPriceId,
  firmHeldAddOnUntilPeriodEnd,
} from '../utils/user-addon.util';
import { addonLogoFromManifest, deubnerAddonLogo } from '../utils/addon-logo.util';
import { billingPeriodLabelKey } from '../common/billing-interval';
import { combinationDiscountAmount } from '../utils/combination.util';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AddonCatalogService {
  private readonly seatOptionsService = inject(SeatOptionsService);
  private readonly seatUtils = inject(SeatUtilsService);
  private readonly userSeatService = inject(UserSeatService);
  private readonly translator = inject(TranslateService);

  buildCatalog(
    connectors: readonly ConnectorDefinition[],
    users: readonly UserWithSeat[],
  ): AddonCatalogItem[] {
    const items: AddonCatalogItem[] = [];

    const contentAddons = [
      { product: this.seatOptionsService.deubnerAddon(), descriptionKey: 'pages.settings.addons.deubnerDescription' },
      { product: this.seatOptionsService.deubnerTaxAddon(), descriptionKey: 'pages.settings.addons.deubnerTaxDescription' },
    ];
    for (const { product, descriptionKey } of contentAddons) {
      const item = this.mapContentAddon(product, descriptionKey, users);
      if (item) {
        items.push(item);
      }
    }

    for (const connector of connectors) {
      const item = this.mapConnector(connector, users);
      if (item) {
        items.push(item);
      }
    }

    return this.applyIntegrationBookingLimit(this.applyCombinationPrices(items));
  }

  /**
   * The price an add-on adds to the bill once its combination partners are booked: the whole
   * combination discount comes off it (Stripe puts it on one line). With everything booked, it
   * shows on the combination's first product, where Stripe puts it on a tie.
   */
  private applyCombinationPrices(items: AddonCatalogItem[]): AddonCatalogItem[] {
    const productOf = (item: AddonCatalogItem): Product | null =>
      item.kind === 'content_addon'
        ? item.product
        : this.seatUtils.findPriceAndProductByPriceId(item.stripePriceId)?.product ?? null;
    const unitAmounts = new Map<string, number>();
    for (const item of items) {
      const product = productOf(item);
      if (product && item.unitAmount != null) {
        unitAmounts.set(product.id, item.unitAmount);
      }
    }
    const held = items.filter((item) => item.booked).map(productOf);

    return items.map((item) => {
      const product = productOf(item);
      const combination = this.seatOptionsService.combinationFor(product, held);
      if (!product || !combination || item.unitAmount == null) {
        return item;
      }
      if (item.booked && combination.product_ids[0] !== product.id) {
        return item;
      }
      const amounts = combination.product_ids.map((id) => unitAmounts.get(id));
      if (amounts.some((amount) => amount == null)) {
        return item;
      }
      const full = (amounts as number[]).reduce((total, amount) => total + amount, 0);
      const discount = combinationDiscountAmount(amounts as number[], combination);
      return {
        ...item,
        discountedPriceLabel: this.formatPriceLabel(Math.max(item.unitAmount - discount, 0)),
        discountTooltip: this.translator.instant('pages.settings.addons.combinationTooltip', {
          products: this.seatOptionsService.combinationProductNames(combination),
          total: this.formatPriceLabel(full - discount),
          full: this.formatPriceLabel(full),
          percent: combination.percent_off,
        }),
      };
    });
  }

  private mapContentAddon(
    product: Product | null,
    descriptionKey: string,
    users: readonly UserWithSeat[],
  ): ContentAddonCatalogItem | null {
    const stripePriceId = this.seatUtils.bookingPriceId(product);
    if (!product || !stripePriceId) {
      return null;
    }
    const heldPriceId = firmHeldAddOnPriceId(users, product.price_ids);
    const price = this.displayPrice(stripePriceId, heldPriceId);
    const deubnerLogo = deubnerAddonLogo();
    const booked = !!heldPriceId;
    return {
      kind: 'content_addon',
      id: `content:${product.id}`,
      product,
      stripePriceId,
      name: product.name ?? 'Deubner',
      description: this.translator.instant(descriptionKey),
      tag: this.translator.instant('pages.settings.addons.tagKnowledgeBase'),
      logoUrl: deubnerLogo.logoUrl,
      logoBlockColor: deubnerLogo.blockColor,
      priceLabel: price ? this.formatPriceLabel(price.unit_amount ?? 0) : undefined,
      pricePeriodKey: billingPeriodLabelKey(price?.interval),
      unitAmount: price?.unit_amount,
      assignable: true,
      booked,
      bookable: !booked,
    };
  }

  /**
   * At most one practice-management connector may be booked.
   * Content add-ons (Deubner) and connectors with premium.bookingLimitExempt stay bookable alongside it.
   */
  applyIntegrationBookingLimit(items: AddonCatalogItem[]): AddonCatalogItem[] {
    const integrationSlotTaken = items.some(
      (item) =>
        item.kind === 'connector'
        && item.booked
        && !this.isExemptFromIntegrationBookingLimit(item),
    );

    return items.map((item) => {
      if (item.booked) {
        return { ...item, bookable: false };
      }
      if (this.isExemptFromIntegrationBookingLimit(item)) {
        return { ...item, bookable: item.assignable };
      }
      return {
        ...item,
        bookable: item.assignable && !integrationSlotTaken,
      };
    });
  }

  private isExemptFromIntegrationBookingLimit(item: AddonCatalogItem): boolean {
    if (item.kind === 'content_addon') {
      return true;
    }
    return connectorBookingLimitExempt(item.connector.manifest_json);
  }

  getBookedConnectors(
    connectors: readonly ConnectorDefinition[],
  ): ConnectorDefinition[] {
    return connectors.filter((connector) => this.userHasConnectorRuntimeAccess(connector));
  }

  isConnectorPurchased(
    connector: ConnectorDefinition,
    users: readonly UserWithSeat[],
  ): boolean {
    return this.firmOwnsConnectorAddOn(connector, users);
  }

  /** Active billable connectors that no firm user still has (or still pays for) an extension for. */
  connectorsToDeactivateWithoutExtension(
    connectors: readonly ConnectorDefinition[],
    users: readonly UserWithSeat[],
  ): ConnectorDefinition[] {
    return connectors.filter((connector) => {
      const priceIds = this.seatOptionsService.integrationPriceIdsForRid(connector.rid);
      if (priceIds.length === 0 || !connector.instances?.some((inst) => inst?.active)) {
        return false;
      }
      return !firmHasAddOnEntitlementForPriceIds(users, priceIds);
    });
  }

  /** Unbooked add-on that stays usable until the current billing period ends. */
  isConnectorCancellationPending(
    connector: ConnectorDefinition,
    users: readonly UserWithSeat[],
  ): boolean {
    return firmHeldAddOnUntilPeriodEnd(
      users,
      this.seatOptionsService.integrationPriceIdsForRid(connector.rid),
    );
  }

  /** Whether any firm user holds the connector's add-on, at whichever price they bought it at. */
  private firmOwnsConnectorAddOn(
    connector: ConnectorDefinition,
    users: readonly UserWithSeat[],
  ): boolean {
    return firmHasAddOnForPriceIds(
      users,
      this.seatOptionsService.integrationPriceIdsForRid(connector.rid),
    );
  }

  /**
   * Billable integrations stay inactive until at least one firm user has the extension; an
   * unbooked one stays usable until the period ends. Unchanged when there is no Stripe add-on.
   */
  effectiveConnectorActive(
    connector: ConnectorDefinition,
    users: readonly UserWithSeat[],
    requestedActive: boolean,
  ): boolean {
    if (!requestedActive) {
      return false;
    }
    const priceIds = this.seatOptionsService.integrationPriceIdsForRid(connector.rid);
    if (priceIds.length === 0) {
      return true;
    }
    return firmHasAddOnEntitlementForPriceIds(users, priceIds);
  }

  userHasConnectorRuntimeAccess(connector: ConnectorDefinition): boolean {
    return hasConnectorFeature(
      this.userSeatService.user()?.enabled_features ?? undefined,
      connector.rid,
    );
  }

  private mapConnector(
    connector: ConnectorDefinition,
    users: readonly UserWithSeat[],
  ): ConnectorAddonCatalogItem | null {
    const manifest = connector.manifest_json;
    if (!isConnectorInFirmAddonSurface(
      manifest,
      connector.rid,
      this.userSeatService.lawFirm()?.enabled_features ?? undefined,
    )) {
      return null;
    }

    const stripePriceId = this.seatOptionsService.integrationPriceIdForRid(connector.rid);
    if (!stripePriceId) {
      console.warn(
        `[addons] No Stripe add-on product for connector "${connector.rid}". `
        + 'Set metadata.features on the Stripe add_on product to the full connector RID (e.g. connector.advoware.de).',
      );
    }

    const heldPriceId = firmHeldAddOnPriceId(
      users,
      this.seatOptionsService.integrationPriceIdsForRid(connector.rid),
    );
    const price = this.displayPrice(stripePriceId, heldPriceId);

    const lang = this.translator.currentLang || 'en';
    const description = this.translateManifestField(manifest?.['description'], lang);
    const logo = addonLogoFromManifest(manifest, connector.rid);
    const bookingLimitExempt = connectorBookingLimitExempt(manifest);

    return {
      kind: 'connector',
      id: `connector:${connector.id}`,
      connector,
      rid: connector.rid ?? '',
      stripePriceId,
      name: connectorIntegrationDisplayLabel(connector, lang),
      description,
      instructionsUrl: connectorInstructionsManualUrl(manifest, lang),
      tag: this.translator.instant(
        bookingLimitExempt
          ? 'pages.settings.addons.tagKnowledgeBase'
          : 'pages.settings.addons.tagPracticeManagement',
      ),
      logoUrl: logo?.logoUrl,
      logoBlockColor: logo?.blockColor,
      priceLabel: price ? this.formatPriceLabel(price.unit_amount ?? 0) : undefined,
      pricePeriodKey: billingPeriodLabelKey(price?.interval),
      unitAmount: price?.unit_amount,
      assignable: !!stripePriceId,
      booked: !!heldPriceId,
      bookable: false,
    };
  }

  /**
   * The price to put on the card: what the firm pays once it holds the add-on, otherwise what it
   * would be sold at. A repriced add-on is still billed at the archived price it was bought at.
   */
  private displayPrice(bookingPriceId: string | undefined, heldPriceId: string | undefined) {
    return this.seatUtils.findPriceAndProductByPriceId(heldPriceId ?? bookingPriceId)?.price;
  }

  private formatPriceLabel(unitAmountCents: number): string {
    return this.seatUtils.formatPrice(unitAmountCents);
  }

  private translateManifestField(value: unknown, lang: string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      const obj = value as Record<string, string>;
      return obj[lang] ?? obj['en'] ?? '';
    }
    return '';
  }
}
